/**
 * Perhitungan uang — SATU sumber kebenaran untuk kasir dan server.
 *
 * Kasir menghitung total secara lokal supaya bisa bekerja tanpa internet.
 * Server menghitung ulang saat pesanan masuk, lalu membandingkan. Kalau
 * berbeda, salah satu sisi punya katalog basi dan pesanan ditolak dengan
 * pesan yang jelas — bukan diterima diam-diam dengan angka yang salah.
 *
 * Karena itu berkas ini WAJIB dipakai kedua sisi. Menyalin rumusnya ke
 * tempat lain adalah cara paling pasti membuat tutup buku tidak cocok.
 *
 * Semua nominal BIGINT rupiah penuh. Tidak ada desimal, tidak ada float:
 * 0.1 + 0.2 tidak sama dengan 0.3, dan pada uang hal itu tidak bisa ditawar.
 */

export interface BarisHitung {
  hargaSatuan: number;
  qty: number;
  hargaModifier: number;
  diskonBaris: number;
  kenaPajak: boolean;
}

export interface AturanOutlet {
  /** Persen, mis. 10 untuk 10%. */
  pb1Persen: number;
  servicePersen: number;
  /** true = harga menu SUDAH termasuk pajak (harga tempel). */
  pb1TermasukHarga: boolean;
  /** 0 = tanpa pembulatan. 100 = bulatkan ke atas ke Rp100 terdekat. */
  pembulatan: number;
}

export interface RincianTotal {
  subtotal: number;
  subtotalKenaPajak: number;
  diskon: number;
  serviceCharge: number;
  pb1: number;
  pembulatan: number;
  total: number;
}

/** Total satu baris pesanan, sebelum pajak. */
export function totalBaris(b: BarisHitung): number {
  const kotor = (b.hargaSatuan + b.hargaModifier) * b.qty;
  return Math.max(0, kotor - b.diskonBaris);
}

/**
 * Urutan perhitungan penting dan tidak boleh ditukar:
 *   subtotal → diskon → service charge → PB1 → pembulatan
 *
 * PB1 dihitung di atas (subtotal setelah diskon + service charge), sesuai
 * praktik yang berlaku di restoran Indonesia. Service charge adalah
 * pendapatan restoran; PB1 bukan — ia dipungut dari pelanggan untuk disetor
 * ke kas daerah, jadi tidak boleh masuk laporan penjualan sebagai pendapatan.
 */
export function hitungTotal(
  baris: BarisHitung[],
  aturan: AturanOutlet,
  diskonPesanan = 0,
): RincianTotal {
  let subtotal = 0;
  let subtotalKenaPajak = 0;

  for (const b of baris) {
    const t = totalBaris(b);
    subtotal += t;
    if (b.kenaPajak) subtotalKenaPajak += t;
  }

  const diskon = Math.min(Math.max(0, Math.round(diskonPesanan)), subtotal);

  // Diskon pesanan mengurangi dasar pajak secara proporsional. Tanpa ini,
  // diskon 50% pada bon campuran akan menghasilkan pajak yang terlalu besar.
  const rasio = subtotal > 0 ? (subtotal - diskon) / subtotal : 0;
  const dasar = Math.round(subtotalKenaPajak * rasio);

  if (aturan.pb1TermasukHarga) {
    // Harga tempel: pajak sudah ada DI DALAM harga, tinggal diurai keluar.
    const pengali = 1 + aturan.pb1Persen / 100;
    const pb1 = Math.round(dasar - dasar / pengali);
    const total = subtotal - diskon;
    return {
      subtotal, subtotalKenaPajak, diskon,
      serviceCharge: 0, pb1, pembulatan: 0, total,
    };
  }

  const serviceCharge = Math.round((subtotal - diskon) * aturan.servicePersen / 100);
  const serviceKenaPajak = Math.round(dasar * aturan.servicePersen / 100);
  const pb1 = Math.round((dasar + serviceKenaPajak) * aturan.pb1Persen / 100);

  const sebelumBulat = subtotal - diskon + serviceCharge + pb1;
  let pembulatan = 0;
  let total = sebelumBulat;

  if (aturan.pembulatan > 0) {
    total = Math.ceil(sebelumBulat / aturan.pembulatan) * aturan.pembulatan;
    pembulatan = total - sebelumBulat;
  }

  return { subtotal, subtotalKenaPajak, diskon, serviceCharge, pb1, pembulatan, total };
}

/** Format rupiah untuk layar dan struk. */
export function rupiah(n: number): string {
  return 'Rp' + Math.round(n).toLocaleString('id-ID');
}

/**
 * Nomor struk: <kodeTerminal>-<YYMMDD>-<urut 4 digit>
 *
 * Urutan dihitung PER TERMINAL, bukan per outlet. Dua kasir yang sama-sama
 * offline tidak bisa berkoordinasi, jadi penomoran per outlet akan
 * menghasilkan struk kembar. Keunikan dijamin indeks (terminal_id, nomor_struk).
 */
export function nomorStruk(kodeTerminal: string, urut: number, tgl = new Date()): string {
  const y = String(tgl.getFullYear()).slice(2);
  const m = String(tgl.getMonth() + 1).padStart(2, '0');
  const d = String(tgl.getDate()).padStart(2, '0');
  return `${kodeTerminal}-${y}${m}${d}-${String(urut).padStart(4, '0')}`;
}
